import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useProcess } from '@fasstech/oip-starter-utils/app';
import {
  all,
  append,
  assoc,
  compose,
  cond,
  equals,
  filter,
  flatten,
  fromPairs,
  includes,
  isEmpty,
  length,
  map,
  pick,
  pluck,
  prop,
  propEq,
  propOr,
  T,
  uniqBy,
  without
} from 'ramda';
import { isNotNilOrEmpty } from '@@serverHelpers/index.mjs';
import {
  OPTION_SELECTION_MODE,
  isOptionSelectionRequired
} from '@@views/_common/server/helpers/groupProtection/validateOptionSelection.mjs';
import useSaveSimulationWarrantiesGroupProtectionMutation from './useSaveSimulationWarrantiesGroupProtectionMutationQuery.js';

const preSelectedCodes = compose(map(prop('optionCode')), filter(propEq('isPreSelected', true)));

const useSimulationWarrantiesGroupProtectionForm = ({
  getSimulationWarrantiesGroupProtection,
  getMiddleNumber
}) => {
  const { onFormSubmit: saveWarranties } = useSaveSimulationWarrantiesGroupProtectionMutation();
  const { processHandlers, context } = useProcess();
  const transitionContext = { context: { projectId: context?.projectId } };

  const isApplicable = propOr(false, 'isApplicable', getSimulationWarrantiesGroupProtection);
  const commission = propOr({}, 'commission', getSimulationWarrantiesGroupProtection);
  const commissionRates = propOr([], 'rates', commission);

  // PricingSelector lit isConformCCN : sans notion de CCN en PREV COLL, toute formule est conforme
  const pricings = map(
    assoc('isConformCCN', true),
    propOr([], 'pricings', getSimulationWarrantiesGroupProtection)
  );

  const preSelectedPricings = filter(propEq('isPreSelected', true), pricings);

  const [selectedPricingCodes, setSelectedPricingCodes] = useState(
    pluck('pricingLabel', preSelectedPricings)
  );
  const [selectedOptions, setSelectedOptions] = useState(
    fromPairs(
      map(
        (pricing) => [prop('pricingLabel', pricing), preSelectedCodes(propOr([], 'options', pricing))],
        preSelectedPricings
      )
    )
  );
  const [loading, setLoading] = useState(false);

  const form = useForm({
    mode: 'onChange',
    defaultValues: { commission: prop('rate', commission) }
  });

  const maxNumberOfProducts = getMiddleNumber(
    'NIVEAUXGARANTIESETTARIFS_GENERAL_MAX_NUMBER_OF_PRODUCTS',
    1
  );

  // Reçoit un updater (comme PricingSelector) ; une formule désélectionnée perd ses options
  const onSelectPricings = (updater) => {
    const nextPricingCodes = updater(selectedPricingCodes);

    setSelectedPricingCodes(nextPricingCodes);
    setSelectedOptions((prev) => pick(nextPricingCodes, prev));
  };

  // Union des options des formules, dans l'ordre reçu : une ligne par option
  const options = compose(
    map(({ optionCode, optionName }) => ({ value: optionCode, label: optionName })),
    uniqBy(prop('optionCode')),
    flatten,
    map(propOr([], 'options'))
  )(pricings);

  const isSelected = (pricingLabel) => includes(pricingLabel, selectedPricingCodes);

  // Règles IND_SELECT_MULTIPLE_OPTION : UNIQUE_OBLIGATOIRE = 1 option sans décochage,
  // UNIQUE_FACULTATIF = 0..1, toute autre valeur = 0..n
  const toggleOption = (pricingLabel, optionCode, mode) => {
    setSelectedOptions((prev) => {
      const current = propOr([], pricingLabel, prev);
      const isOptionSelected = includes(optionCode, current);

      const updateSelection = cond([
        [equals(OPTION_SELECTION_MODE.UNIQUE_REQUIRED), () => [optionCode]],
        [
          equals(OPTION_SELECTION_MODE.UNIQUE_OPTIONAL),
          () => (isOptionSelected ? [] : [optionCode])
        ],
        [T, () => (isOptionSelected ? without([optionCode], current) : append(optionCode, current))]
      ]);

      return assoc(pricingLabel, updateSelection(mode), prev);
    });
  };

  const selectedPricings = filter(
    (pricing) => includes(prop('pricingLabel', pricing), selectedPricingCodes),
    pricings
  );

  const isPricingComplete = (pricing) =>
    !isOptionSelectionRequired(prop('optionsSelectionMode', pricing)) ||
    isEmpty(propOr([], 'options', pricing)) ||
    equals(1, length(propOr([], prop('pricingLabel', pricing), selectedOptions)));

  const isSelectionComplete = isNotNilOrEmpty(selectedPricings) && all(isPricingComplete, selectedPricings);

  const $onClickPrev = () => {
    processHandlers.onPrev(transitionContext);
  };

  const onSubmit = (values) => {
    if (!isApplicable) {
      processHandlers.onNext(transitionContext);
      return;
    }

    if (loading) return;
    setLoading(true);

    saveWarranties(
      {
        pricings: map(
          (pricing) => ({
            pricingCode: prop('pricingCode', pricing),
            optionCodes: propOr([], prop('pricingLabel', pricing), selectedOptions)
          }),
          selectedPricings
        ),
        commission: isEmpty(commissionRates) ? undefined : prop('commission', values)
      },
      (ok, error) => {
        if (ok && !error) {
          processHandlers.onNext(transitionContext);
        } else {
          setLoading(false);
        }
      }
    );
  };

  return {
    form,
    isApplicable,
    pricings,
    options,
    commissionRates,
    maxNumberOfProducts,
    isSelected,
    selectedPricingCodes,
    onSelectPricings,
    selectedOptions,
    toggleOption,
    isSelectionComplete,
    onSubmit,
    $onClickPrev
  };
};

export default useSimulationWarrantiesGroupProtectionForm;
